import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../ReduxToolkit/Store';
import { toggleFavorite, fetchFavorites } from '../ReduxToolkit/FavoriteSlice';
import { fetchFolders } from '../ReduxToolkit/FolderSlice';
import { logout } from '../ReduxToolkit/Auth/AuthSlice';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './AdminRoute';
import AppLayout from '../Layout/AppLayout';

// Vues
import { HomeView } from '../components/views/HomeView';
import { SearchView } from '../components/views/SearchView';
import { CategoryView } from '../components/views/CategoryView';
import { DocumentDetailView } from '../components/views/DocumentDetailView';
import { UserDashboardView } from '../components/views/UserDashboardView';
import { AuthModalProvider } from '../contexts/AuthModalContext';
import { AdminView } from '../components/views/AdminView';
import VerifyAccountView from '../components/views/VerifyAccountView';
import { RegisterView } from '../components/views/RegisterView';
import FormulasView from '../components/views/FormulasView';
import { suggestionService } from '../Services/SuggestionService';
import { SuggestedDocument } from '../types';

const RouterContent = () => {
  const dispatch = useDispatch<AppDispatch>();
  const favoritesState = useSelector((state: RootState) => state.favorites.items);
  const favoritesArray = (favoritesState || []).map((f: any) => String(f.documentId).toLowerCase().trim());
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  // Handle unauthorized/logout event from ApiClient without reloading the page
  React.useEffect(() => {
    const handleAuthLogout = () => {
      dispatch(logout());
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, [dispatch]);

  React.useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (isAuthenticated && token) {
      dispatch(fetchFavorites());
      dispatch(fetchFolders());
    }
  }, [dispatch, isAuthenticated]);

  const handleToggleFavorite = async (docId: string | number) => {
    await dispatch(toggleFavorite(docId) as any);
  };
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchFilters, setSearchFilters] = React.useState<any>({});
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = React.useState<SuggestedDocument[]>(() => suggestionService.getAll());

  const handleAddSuggestion = (newSugg: Omit<SuggestedDocument, 'id' | 'status' | 'submittedAt'>) => {
    const created = suggestionService.add(newSugg);
    setSuggestions(suggestionService.getAll());
    return created;
  };

  const handleApproveSuggestion = (id: string) => {
    suggestionService.approve(id);
    setSuggestions(suggestionService.getAll());
  };

  const setView = (view: string) => {
    if (view === 'search') navigate('/search');
    else if (view === 'home') navigate('/');
    else if (view === 'categories') navigate('/categories');
    else if (view === 'register') navigate('/register');
    else if (view === 'formulas' || view === 'formules') navigate('/formules');
    else navigate(`/${view}`);
  };

  return (
    <Routes>
      {/* Main Layout routes with full persistent navigation Header */}
      <Route path="/" element={<AppLayout />}>
        <Route index element={<HomeView documents={[]} setView={setView} setActiveDocumentId={() => {}} setSearchQueryState={setSearchQuery} setSearchFiltersState={setSearchFilters} addSuggestion={handleAddSuggestion} />} />
        <Route path="search" element={<SearchView documents={[]} setView={setView} setActiveDocumentId={() => {}} searchQuery={searchQuery} setSearchQuery={setSearchQuery} searchFilters={searchFilters} setSearchFilters={setSearchFilters} favorites={favoritesArray} toggleFavorite={handleToggleFavorite} />} />
        <Route path="categories" element={<CategoryView documents={[]} setView={setView} setActiveDocumentId={() => {}} setSearchQueryState={setSearchQuery} setSearchFiltersState={setSearchFilters} selectedOrigin="NATIONAL" setSelectedOrigin={() => {}} />} />
        <Route path="document/:id" element={<DocumentDetailView document={null as any} setView={setView} setActiveDocumentId={() => {}} allDocuments={[]} favorites={favoritesArray} toggleFavorite={handleToggleFavorite} addDownload={() => {}} />} />
        <Route path="register" element={<RegisterView />} />
        <Route path="formules" element={<FormulasView />} />
        <Route path="formulas" element={<Navigate to="/formules" replace />} />
        
        <Route path="auth" element={<Navigate to="/" replace />} />
        <Route path="auth/verify" element={<VerifyAccountView />} />
        <Route path="auth/verify/:token" element={<VerifyAccountView />} />

        {/* Routes privées utilisateur */}
        <Route element={<PrivateRoute />}>
          <Route path="dashboard" element={<UserDashboardView documents={[]} setView={setView} setActiveDocumentId={() => {}} favorites={favoritesArray} toggleFavorite={handleToggleFavorite} downloads={[]} suggestions={suggestions} />} />
        </Route>

        {/* Route d'administration protégée : redirection automatique vers / si non autorisé */}
        <Route element={<AdminRoute />}>
          <Route path="admin" element={<AdminView documents={[]} addDocument={() => {}} updateDocument={() => {}} deleteDocument={() => {}} suggestions={suggestions} approveSuggestion={handleApproveSuggestion} users={[]} setUsers={() => {}} />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};

const RouterData = () => {
  return (
    <BrowserRouter>
      <AuthModalProvider>
        <RouterContent />
      </AuthModalProvider>
    </BrowserRouter>
  );
};

export default RouterData;